import React, { useEffect, useState, useMemo } from 'react';
import Helmet from 'react-helmet';
import moment from 'moment';

import Config from 'lib/Config';
import { PageHelper } from 'lib/helpers/PageHelper';
import { RequestStatus } from 'lib/RequestManager';
import { ProviderNames } from 'lib/data/models/Providers';
import { EventGroupBy } from 'lib/types/EventGroupBy';
import { getAccountProviders, getAccountTimezone, getInnerError, renderLoader } from 'lib/utils/adaya/AdayaUtils';

import { getStoreDate } from 'utils/DateUtils';
import { getLogger } from 'utils/logging';

import LPManagerHeader from './header';
import LPManagerBody from './body';
import { sortLandingPages } from './utils';
import './style.scss';

const { log } = getLogger('LPManager', { color: 'yellow', enabled: false });

const pageHelper = new PageHelper();

function LPManager(props) {
    const user = props.auth.user;
    const account = user?.activeAccount;
    const timezone = getAccountTimezone(account);
    const providers = getAccountProviders(account, [ProviderNames.SHOPIFY]).map(p => p.name);

    const [metricOption, setMetricOption] = useState('ROAS');
    const [performanceOption, setPerformanceOption] = useState('Highest');
    const [dateRange, setDateRange] = useState([moment(), moment()]);

    const [{ data, totals }, setResponse] = useState({ data: [], totals: {} }); // raw data for current date range (before filters);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);

    function getFilterFromSelection() {
        const timeRange = {
            start_date: getStoreDate(dateRange[0], timezone).startOf('day').toISOString(),
            end_date: getStoreDate(dateRange[1], timezone).endOf('day').toISOString()
        };
        if (!account) throw `No account found for the current user.`;
        return { account, timeRange, providers, groupBy: EventGroupBy.LandingPage };
    };

    function fetchData(fetchNewData = false) {
        const filter = getFilterFromSelection();
        setLoading(true);

        pageHelper
            .getDailyTrackingDataRequest(account, filter, {
                dontFetchNewData: !fetchNewData, // QAM won't ask server for data by defaultErrorMessage
                resolveUnknownObjects: true, // let QAM resolve the unknown ad object IDs->names
                clearDataBefore: false,
                forceUpdate: false
            })
            .then((res) => {
                onDataResponse(res);
            })
            .catch((err) => {
                console.log('error log:', err);
                setError(getInnerError(err));
            }).finally(() => {
                setLoading(false);
            });
    }

    function onDataResponse(r) {
        try {
            if (!r) throw 'Response was empty.';
            log('onDataResponse', r);

            if (r.status == RequestStatus.FINISHED) {
                // update UI data if the response matches the current request filter
                if (pageHelper.requestMatchesKey(r, getFilterFromSelection())) {
                    log(`Raw response:`, r.response, typeof r.response == 'object' ? `${Object.keys(r.response).length} days` : '(no data)');
                    setResponse(r.response);
                } else {
                    log('Current request response does not match current select, not showing data. Request key:', r.key);
                }
            } else {
                // shouldn't get here, it's handled through exceptions mostly, but just in case.
                setError(getInnerError(r));
            }
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    };

    function onRefresh() {
        fetchData(true);
    }

    // fetch data
    useEffect(() => {
        fetchData();
    }, [dateRange]);

    const filteredData = useMemo(() => sortLandingPages(data, performanceOption, metricOption, user).filter(page => page.id), [data, metricOption, performanceOption]);

    return (
        <div className="lp-manager-page">
            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Landing Page Manager</title></Helmet>

            <LPManagerHeader
                metricOption={metricOption}
                performanceOption={performanceOption}
                dateRange={dateRange}
                onMetricOptionChange={setMetricOption}
                onPerformanceOptionChange={setPerformanceOption}
                onDateRangeChange={setDateRange}
                onRefresh={onRefresh}
            />

            {isLoading ? renderLoader() : <LPManagerBody data={filteredData} totals={totals} />}
        </div>
    );
};

export default LPManager;